import React from 'react';

import { cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { Box } from '@atlaskit/primitives/compiled';
import RadioGroup from '@atlaskit/radio/radio-group';
import TextArea from '@atlaskit/textarea/text-area';
import { token } from '@atlaskit/tokens';

import { OTHER_ID } from '../../../../../../../../common/types/form/form-fields/field-values';
import {
	asFormOptionField,
	toRadioOptions,
} from '../../../../../../../../common/types/form/form-fields/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import { useFormField } from '../../../../../../../../controllers/form-renderer-store';
import { messages } from './messages';

export const ViewRadioInput = ({
	fieldId,
	fieldInputId,
	fieldLabelId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useFormField);
	const formField = useFormField({ fieldId });
	const formOptionField = asFormOptionField(formField);
	const intl = useIntl();

	if (formOptionField === undefined) {
		return <></>;
	}

	const options = formOptionField.hasOtherOption
		? toRadioOptions([
				...formOptionField.options,
				{ id: OTHER_ID, label: intl.formatMessage(messages.otherOption) },
			])
		: toRadioOptions(formOptionField.options);

	return (
		<Box testId="field-input-radio-view">
			{formOptionField.hasOtherOption || formOptionField.options.length ? (
				<Box>
					<RadioGroup
						id={fieldInputId}
						key={fieldInputId}
						name={fieldInputId}
						labelId={fieldLabelId}
						options={options.map((option) => ({
							...option,
							label: <Box xcss={styles.radioLabel}>{option.label}</Box>,
						}))}
						value={
							formOptionField.value?.otherOptionText
								? OTHER_ID
								: (formOptionField.value?.options[0]?.id ?? null)
						}
						isDisabled
						testId={`field-input-radio-view-${fieldId}`}
					/>
				</Box>
			) : (
				<Box xcss={styles.placeholderStyles} />
			)}
			{formOptionField.value?.otherOptionText && (
				<TextArea
					isDisabled
					value={formOptionField.value.otherOptionText}
					aria-label={intl.formatMessage(messages.otherOptionTextLabel, { label: formField.label })}
				/>
			)}
		</Box>
	);
};

const styles = cssMap({
	radioStyles: {
		color: token('color.text'),
	},
	placeholderStyles: {
		marginTop: token('space.500'),
		marginRight: token('space.500'),
		marginBottom: token('space.500'),
		marginLeft: token('space.500'),
	},
	radioLabel: {
		color: token('color.text'),
	},
});
